import { For, Show, createMemo } from 'solid-js';
import clsx from 'clsx';
import { ErrorCatcher } from '../../tools/error-catcher';
import { ReadMoreItem } from './read-more-item/read-more-item';
import { ImageWordpress } from '../../types/shared';
import { useAppContext } from '../../app-context-provider/app-context-provider';
import { GET_POSTS_QUERY_WITH_TYPE_FILTER } from '../blog-archive/blog-archive-queries';

export type ReadMoreProps = {
    selectedPosts: {
        post_title: string;
        featured_image: ImageWordpress;
        preamble: string;
        permalink: string;
    }[];
    categories: {
        name: string;
        id: number;
        slug: string;
        postType?: string;
    }[];
    currentPost: number;
    postType: string;
    postCount?: number;
    selectedPostTypes?: Record<string, boolean>;
    index: number;
    blockName: string;
    hash: string;
};

// Normalized post type for unified rendering
type NormalizedPost = {
    permalink: string;
    post_title: string;
    preamble: string;
    featured_image: ImageWordpress;
    type?: string;
    isManual?: boolean;
};

export const ReadMore = (componentProps: ReadMoreProps) => {
    const { createCachedResource } = useAppContext();

    // Get the post count (default 3)
    const postCount = () => componentProps.postCount || 3;

    // Get array of selected post types
    const selectedPostTypes = createMemo(() => {
        const types = componentProps.selectedPostTypes || {};
        const selected = Object.keys(types).filter((type) => types[type]);

        // If no selected post types, return current posts post type
        return selected.length ? selected : [componentProps.postType];
    });

    // Get category slugs for selected post types
    const tagsForSelectedTypes = createMemo(() => {
        const types = selectedPostTypes();
        const categories = componentProps.categories || [];
        return categories
            .filter(c => c.postType && types.includes(c.postType))
            .map(c => c.slug);
    });

    // Calculate how many dynamic posts we need
    const manualPostsCount = () => (componentProps.selectedPosts || []).length;
    const dynamicPostsNeeded = () => Math.max(0, postCount() - manualPostsCount());

    // WordPress post type to content-next type mapping
    // WordPress registers 'clients' but content-next stores as 'client'
    const wpToContentType = (type: string) => (type === 'clients' ? 'client' : type);

    // Build query variables - only query if we need dynamic posts
    const queryVars = createMemo(() => {
        const types = selectedPostTypes();
        const needed = dynamicPostsNeeded();

        // Don't query if we don't need dynamic posts
        if (needed === 0) {
            return;
        }

        const tags = tagsForSelectedTypes();

        return {
            type: types.map(wpToContentType),
            tags: tags.length > 0 ? tags : [],
            // Fetch more than needed to allow for filtering and distribution
            limit: Math.max(10, needed * 3),
            offset: 0,
        };
    });

    // Fetch dynamic posts
    const [postsContent] = createCachedResource(
        GET_POSTS_QUERY_WITH_TYPE_FILTER,
        () => queryVars(),
        true
    );

    // Distribute posts ensuring at least one from each selected type
    const distributeByType = (posts: any[], selectedTypes: string[], count: number): any[] => {
        if (posts.length === 0 || count === 0) return [];

        // Group posts by type
        const byType: Record<string, any[]> = {};
        selectedTypes.forEach(t => { byType[t] = []; });

        posts.forEach(post => {
            const type = post.content?.type;
            if (type && byType[type]) {
                byType[type].push(post);
            }
        });

        const result: any[] = [];

        // Phase 1: Round-robin - take one from each type to ensure representation
        for (const type of selectedTypes) {
            if (result.length >= count) break;
            if (byType[type].length > 0) {
                const post = byType[type].shift();
                if (post) result.push(post);
            }
        }

        // Phase 2: Fill remaining slots from all remaining posts
        // Posts are already sorted DESC by the GraphQL query
        const remaining = Object.values(byType).flat();
        while (result.length < count && remaining.length > 0) {
            const post = remaining.shift();
            if (post) result.push(post);
        }

        return result;
    };

    // Get permalinks of manually selected posts for deduplication
    const manualPostPermalinks = createMemo(() => {
        const manual = componentProps.selectedPosts || [];
        return new Set(manual.map(post => post.permalink).filter(Boolean));
    });

    // Get filtered dynamic posts (excluding current post, manual posts, and clients without content)
    const filteredDynamicPosts = createMemo(() => {
        const content = postsContent();
        if (!content?.posts) return [];

        const manualPermalinks = manualPostPermalinks();

        return content.posts.filter((post: any) => {
            // Exclude current post
            if (post.content?.id === componentProps.currentPost) return false;
            // Exclude posts already selected manually
            if (manualPermalinks.has(post.content?.permalink)) return false;
            // Exclude clients without post content
            if (post.content?.type === 'client' && post.content?.has_post_content === false) return false;
            return true;
        });
    });

    // Get distributed dynamic posts
    const distributedDynamicPosts = createMemo(() => {
        const posts = filteredDynamicPosts();
        const selectedTypes = selectedPostTypes().map(wpToContentType);
        const needed = dynamicPostsNeeded();

        return distributeByType(posts, selectedTypes, needed);
    });

    // Normalize manual posts to common format
    const normalizedManualPosts = createMemo((): NormalizedPost[] => {
        const manual = componentProps.selectedPosts || [];
        return manual.map(post => ({
            permalink: post.permalink,
            post_title: post.post_title,
            preamble: post.preamble,
            featured_image: post.featured_image,
            isManual: true,
        }));
    });

    // Normalize dynamic posts to common format
    const normalizedDynamicPosts = createMemo((): NormalizedPost[] => {
        return distributedDynamicPosts().map((post: any) => ({
            permalink: post.content?.permalink,
            post_title: post.content?.post_title,
            preamble: post.content?.preamble,
            featured_image: post.content?.featured_image,
            type: post.content?.type,
            isManual: false,
        }));
    });

    // Combine manual + dynamic posts, limited to postCount
    const finalContent = createMemo((): NormalizedPost[] => {
        const manual = normalizedManualPosts();
        const dynamic = normalizedDynamicPosts();
        const combined = [...manual, ...dynamic];
        return combined.slice(0, postCount());
    });

    // Grid classes based on post count
    const gridClasses = createMemo(() => {
        const count = postCount();
        const baseClasses = 'post-list gap-y-s gap-x-m grid';

        if (count === 1) {
            return clsx(baseClasses, 'grid-cols-1 max-w-md mx-auto');
        }
        if (count === 2) {
            return clsx(baseClasses, 'grid-cols-1 mobile:grid-cols-2');
        }
        // count === 3 or more
        return clsx(baseClasses, 'grid-cols-1 mobile:grid-cols-2 bigTablet:grid-cols-3');
    });

    return (
        <ErrorCatcher componentName="ReadMoreArticles">
            <Show when={finalContent().length > 0}>
                <section class={gridClasses()}>
                    <For each={finalContent()}>
                        {(post) => (
                            <article class="mb-xl">
                                <ReadMoreItem
                                    permalink={post.permalink}
                                    postTitle={post.post_title}
                                    preamble={post.preamble}
                                    featuredImage={post.featured_image}
                                />
                            </article>
                        )}
                    </For>
                </section>
            </Show>
        </ErrorCatcher>
    );
};

export default ReadMore;
